// Import React modules
import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  getStorage,
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
} from "firebase/storage";
import {
  getAuth,
  signOut,
  reauthenticateWithCredential,
  EmailAuthProvider,
  updatePassword,
  signInWithEmailAndPassword,
} from "firebase/auth";
// Import other modules
import {
  doc,
  getDoc,
  updateDoc,
  onSnapshot,
  collection,
  deleteField,
  query,
  orderBy,
  limit,
  getDocs,
} from "firebase/firestore";
import axios from "axios";
// Import Firebase config
import { auth, db } from "../firebase";
// Import redux components
import { RootState } from "../redux/store";
import { logout, login } from "../redux/features/userSlice";
// Import home components
import TopBar from "../components/TopBar";
import LeftTab from "../components/LeftTab";
import DropdownMenu from "../components/DropdownMenu";
import LetterHeadUpload from "../components/LetterHeadUpload";
import SignatureUpload from "../components/SignatureUpload";
// Import tab contents
import PatientsTabContent from "./PatientsTabContent";
import TranscribeTabContent from "./TranscribeTabContent";
import SearchTabContent from "./SearchTabContent";
import HistoryTabContent from "./HistoryTabContent";
import HelpTabContent from "./HelpTabContent";
import TemplatesTabContent from "./TemplatesTabContent";
import AccountClinicModal from "./AccountClinicModal";
import AccountProfileModal from "./AccountProfileModal";
import AccountSubscriptionModal from "./AccountSubscriptionModal";
import SettingsGeneralModal from "./SettingsGeneralModal";
import SettingsNoteModal from "./SettingsNoteModal";
import BannerNotification from "../components/BannerNotification";
import { MeddbasePID, MeddbasePV1 } from "../types/meddbase";
// Import assets
import xMarkIcon from "../assets/icons/xmark.svg";
// Import CSS stylesheets
import "../styles/variables.css";
import "../styles/layoutStyles.css";
import "../styles/textStyles.css";
import "../styles/buttonStyles.css";
import MigrationBanner from "../components/MigrationBanner";

const AccountAccountTabs = {
  PROFILE: "Profile",
  CLINIC: "Clinic",
  SUBSCRIPTION: "Subscription",
};

const AccountSettingsTabs = {
  GENERAL: "General",
  // NOTE: "Note",
  LETTER: "Letter",
  SIGNATURE: "Signature",
};

interface User {
  id: string;
  account?: string;
  stripeSessionId?: string;
  clinikoApiKey?: string;
  meddbaseUserId?: string;
  isMeddbaseUserIdStored?: boolean;
  cancelled?: string;
  currentPeriodEnd?: string;
  clinicalNoteVoice?: string;
  letterVoice?: string;
  meetingNoteVoice?: string;
  defaultTemplateCategory?: string;
  defaultTemplateId?: string;
  title?: string;
  firstName?: string;
  lastName?: string;
  displayName?: string;
  initials?: string;
  photoUrl?: string;
  dob?: Date;
  sex?: string;
  address1?: string;
  address2?: string;
  address3?: string;
  postcode?: string;
  email?: string;
  phone?: string;
  phoneType?: string;
  occupation?: string;
  speciality?: string;
  clinicName?: string;
  clinicRole?: string;
  clinicAddress1?: string;
  clinicAddress2?: string;
  clinicAddress3?: string;
  clinicPostCode?: string;
  clinicEmail?: string;
  clinicPhone?: string;
  clinicPhoneType?: string;
  meddbaseEnabled?: boolean;
}

function Home() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const user = useSelector((state: RootState) => state.user);
  const [email, setEmail] = useState<string>("");
  const [uid, setUid] = useState<string>("");
  const [userInitials, setUserInitials] = useState<string>("");
  const [userAccount, setUserAccount] = useState<string>("None");
  const [userEmail, setUserEmail] = useState<string>("");
  const [userFirstName, setUserFirstName] = useState<string>("");
  const [userLastName, setUserLastName] = useState<string>("");
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [currentPeriodEnd, setCurrentPeriodEnd] = useState<string>("");
  const [stripeSessionId, setStripeSessionId] = useState<string>("");
  const [selectedTab, setSelectedTab] = useState<string>("transcribe");
  const [selectedAccountModalTab, setSelectedAccountModalTab] =
    useState<string>("Account");
  const [showCancelButton, setShowCancelButton] = useState<boolean>(true);
  const [isAvatarDropdownOpen, setAvatarDropdownOpen] =
    useState<boolean>(false);
  const [isAccountModalOpen, setAccountModalOpen] = useState<boolean>(false);
  const [clinikoApiKey, setClinikoApiKey] = useState<string>("");
  const [isClinikoApiKeyStored, setIsClinikoApiKeyStored] =
    useState<boolean>(false);
  const [isClinikoApiKeyValid, setIsClinikoApiKeyValid] =
    useState<boolean>(true);
  const [meddbaseUserId, setMeddbaseUserId] = useState<string>("");
  const [isMeddbaseUserIdStored, setIsMeddbaseUserIdStored] =
    useState<boolean>(false);
  const [isMeddbaseUserIdValid, setIsMeddbaseUserIdValid] =
    useState<boolean>(true);
  const [recording, setRecording] = useState(false);
  const [showConfirmCancelModal, setShowConfirmCancelModal] =
    useState<boolean>(false);
  const [confirmCancelPassword, setConfirmCancelPassword] =
    useState<string>("");
  const [isCancelingSubscription, setIsCancelingSubscription] = useState(false);
  const [activeAccountTab, setActiveAccountTab] = useState<string>(
    AccountAccountTabs.PROFILE
  );
  const [activeSettingsTab, setActiveSettingsTab] = useState<string>(
    AccountSettingsTabs.GENERAL
  );
  const dropdownMenuRef = useRef<HTMLDivElement>(null);
  const [isLeftTabCollapsed, setIsLeftTabCollapsed] = useState(true);
  const [showMigrationBanner, setShowMigrationBanner] = useState(() => {
    // Check localStorage for the banner state, default to true if not found
    const savedState = localStorage.getItem("migrationBannerVisible");
    return savedState === null ? true : savedState === "true";
  });
  const [showBannerNotification, setShowBannerNotification] = useState(false);
  const [bannerNotificationMessage, setBannerNotificationMessage] =
    useState("");
  const [meddbasePID, setMeddbasePID] = useState<MeddbasePID | null>(null);
  const [meddbasePV1, setMeddbasePV1] = useState<MeddbasePV1 | null>(null);
  const [unactionedHl7Messages, setUnactionedHl7Messages] = useState<
    Array<{ pid: MeddbasePID; pv1: MeddbasePV1; id: string; timestamp: Date }>
  >([]);

  const toggleLeftTab = () => {
    setIsLeftTabCollapsed(!isLeftTabCollapsed);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (window.innerWidth <= 768) {
        const target = event.target as HTMLElement; // Type assertion
        if (
          !target.closest(".left-tab-container") &&
          !target.closest(".top-bar-container") &&
          !isLeftTabCollapsed
        ) {
          toggleLeftTab(); // Assuming toggleLeftTab() can both open and close the left tab
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isLeftTabCollapsed, toggleLeftTab]);

  const handleStartSession = () => {
    setSelectedTab("transcribe");
    handleCloseNotification();
  };

  useEffect(() => {
    if (!uid || !meddbaseUserId) return;

    const messagesCollectionRef = collection(
      db,
      "meddbase",
      "psm",
      "users",
      meddbaseUserId,
      "messages"
    );

    const fetchUnactionedMessages = async () => {
      try {
        // Fetch all unactioned messages
        const qUnactioned = query(
          messagesCollectionRef,
          orderBy("timestamp", "desc")
        );
        const unactionedSnapshot = await getDocs(qUnactioned);
        const unactioned = unactionedSnapshot.docs
          .filter((doc) => {
            const data = doc.data();
            return data.actioned !== true;
          })
          .map((doc) => ({
            pid: doc.data().messageData.pid as MeddbasePID,
            pv1: doc.data().messageData.pv1 as MeddbasePV1,
            id: doc.id,
            timestamp: doc.data().timestamp,
          }));
        setUnactionedHl7Messages(unactioned);

        // Get the latest message from unactioned messages
        if (unactioned.length > 0) {
          const latestMessage = unactioned[0]; // The first message is the latest due to descending order
          const latestPID = {
            ...latestMessage.pid,
            id: latestMessage.id,
            timestamp: latestMessage.timestamp,
          } as MeddbasePID;
          const latestPV1 = {
            ...latestMessage.pv1,
            id: latestMessage.id,
            timestamp: latestMessage.timestamp,
          } as MeddbasePV1;

          setMeddbasePID(latestPID);
          setMeddbasePV1(latestPV1);

          // Store in user document
          const userDocRef = doc(db, "users", uid);
          await updateDoc(userDocRef, {
            currentMeddbasePID: latestPID,
            currentMeddbasePV1: latestPV1,
          });
        }
      } catch (error) {
        console.error("Error fetching messages: ", error);
      }
    };

    fetchUnactionedMessages();

    // Listen for new messages
    const unsubscribe = onSnapshot(
      messagesCollectionRef,
      (snapshot) => {
        snapshot.docChanges().forEach((change) => {
          if (change.type === "added") {
            const newMessage = change.doc.data();

            if (newMessage.messageData.pid) {
              handleNewMessage(newMessage, change.doc.id);
            }
          }
        });
      },
      (error) => {
        console.error("Error listening for messages: ", error);
      }
    );

    return () => unsubscribe();
  }, [uid, meddbaseUserId]);

  const handleNewMessage = (newMessage: any, docId: string) => {
    if (!newMessage.actioned) {
      setUnactionedHl7Messages((prev) => {
        const exists = prev.some((msg) => msg.id === docId);
        setMeddbasePID({
          ...newMessage.messageData.pid,
          id: docId,
          timestamp: newMessage.timestamp,
        } as MeddbasePID);
        setMeddbasePV1({
          ...newMessage.messageData.pv1,
          id: docId,
          timestamp: newMessage.timestamp,
        } as MeddbasePV1);

        // Store meddbasePID and meddbasePV1 in the user record
        const userDocRef = doc(db, "users", uid);
        updateDoc(userDocRef, {
          currentMeddbasePID: {
            ...newMessage.messageData.pid,
            id: docId,
            timestamp: newMessage.timestamp,
          },
          currentMeddbasePV1: {
            ...newMessage.messageData.pv1,
            id: docId,
            timestamp: newMessage.timestamp,
          },
        }).catch((error) => {
          console.error("Error updating user record:", error);
          // Consider adding user-facing error handling here if necessary
        });

        // Show banner notification for recent messages
        if (
          newMessage.timestamp &&
          typeof newMessage.timestamp.toDate === "function"
        ) {
          const messageTimestamp = newMessage.timestamp.toDate();
          const currentTime = new Date();
          const timeDifference =
            currentTime.getTime() - messageTimestamp.getTime();

          if (timeDifference <= 1800000) {
            // 30 minutes in milliseconds
            setBannerNotificationMessage(
              `${newMessage.messageData.pid.patientName.givenName} ${newMessage.messageData.pid.patientName.familyName} has arrived. `
            );
            setShowBannerNotification(true);
          }
        }

        // Add to unactioned messages if not already present
        if (!exists) {
          return [
            ...prev,
            {
              pid: newMessage.messageData.pid as MeddbasePID,
              pv1: newMessage.messageData.pv1 as MeddbasePV1,
              id: docId,
              timestamp: newMessage.timestamp,
            },
          ];
        }
        return prev;
      });
    } else {
      // Remove from unactioned messages if it was there
      setUnactionedHl7Messages((prev) =>
        prev.filter((msg) => msg.id !== docId)
      );
    }
  };

  const handleCloseNotification = () => {
    setShowBannerNotification(false);
  };

  const updateCurrentUser = (updatedUser: User) => {
    setCurrentUser(updatedUser);
  };

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (!user) {
        console.log(
          "[Home] User not authenticated. Redirecting to Welcome page."
        );
        window.Intercom("shutdown");
        window.Intercom("boot", {
          api_base: "https://api-iam.intercom.io",
          app_id: "zdag9apd",
        });
        dispatch(logout());
        navigate("/welcome");
        return;
      }

      console.log("[Home] User details retrieved.");
      setUid(user.uid);
      const userDocRef = doc(db, "users", user.uid);
      const docSnap = await getDoc(userDocRef);

      if (!docSnap.exists()) {
        console.error(
          "[Home] User document not found. Redirecting to Welcome page."
        );
        dispatch(logout());
        navigate("/welcome");
        return;
      }

      const userData = docSnap.data();
      try {
        const response = await axios.post(
          "https://europe-west2-motics-consultations.cloudfunctions.net/intercom-generate-user-hash",
          { userId: user.uid },
          { headers: { "Content-Type": "application/json" } }
        );

        window.Intercom("boot", {
          api_base: "https://api-iam.intercom.io",
          app_id: "zdag9apd",
          name: `${userData.firstName} ${userData.lastName}`,
          email: userData.email,
          user_id: user.uid,
          user_hash: response.data.userHash,
        });
      } catch (error) {
        console.error("Error generating user hash:", error);
      }

      const firstInitial = userData.firstName
        ? userData.firstName.charAt(0).toUpperCase()
        : "";
      const lastInitial = userData.lastName
        ? userData.lastName.charAt(0).toUpperCase()
        : "";
      const userInitials = `${firstInitial}${lastInitial}`;

      const currentUser: User = {
        id: user.uid,
        displayName: user.displayName || "",
        photoUrl: user.photoURL || "",
        initials: userInitials,
        defaultTemplateCategory: userData.defaultTemplateCategory,
        defaultTemplateId: userData.defaultTemplateId,
        ...userData,
      };

      setUserInitials(userInitials);
      if (userData.email) {
        setUserEmail(userData.email);
      }
      setCurrentUser(currentUser);

      if (userData.stripeSessionId) {
        setStripeSessionId(userData.stripeSessionId);
        if (userData.stripeSessionId === "free_trial") {
          setShowCancelButton(false);
        }
      }
      if (userData.currentPeriodEnd) {
        setCurrentPeriodEnd(userData.currentPeriodEnd);
      }
      if (userData.cancelled === "True") {
        setShowCancelButton(false);
      }
      if (userData.clinikoApiKey) {
        setClinikoApiKey(userData.clinikoApiKey);
        setIsClinikoApiKeyStored(true);
      }
      if (userData.meddbaseUserId) {
        setMeddbaseUserId(userData.meddbaseUserId);
        setIsMeddbaseUserIdStored(true);
      }

      dispatch(
        login({
          email: userData.email,
          uid: user.uid,
          displayName: user.displayName || "",
          photoURL: user.photoURL || "",
        })
      );
    });

    return () => unsubscribe();
  }, [dispatch, navigate]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      // Check if the click is outside the left tab options
      if (!target.closest(".tab-option")) {
        setSelectedTab(selectedTab);
      }

      // Check if the click is outside the avatar dropdown
      if (
        dropdownMenuRef.current &&
        !(dropdownMenuRef.current as HTMLElement).contains(target)
      ) {
        setAvatarDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [selectedTab, dropdownMenuRef]);

  const handleSignOut = () => {
    // console.log("[Home] Signing out...", email);
    const auth = getAuth();
    dispatch(logout());
    signOut(auth)
      .then(() => {
        // console.log("[Home] Signed out.");
        window.Intercom("shutdown");
        window.Intercom("boot", {
          api_base: "https://api-iam.intercom.io",
          app_id: "zdag9apd",
        });
        navigate("/welcome");
      })
      .catch((error) => {
        // Add error handling
      });
  };

  const handleAccountClick = () => {
    setAccountModalOpen(true);
    setAvatarDropdownOpen(false);
  };

  const handleAccountModalClose = () => {
    setAccountModalOpen(false);
  };

  const handleAvatarClick = () => {
    setAvatarDropdownOpen((prevState) => !prevState);
  };

  const handleTabSelect = (tab: string) => {
    if (selectedTab === "transcribe" && recording) {
      const confirmLeave = window.confirm(
        "Are you sure you want to leave? Your recording will be lost."
      );
      if (!confirmLeave) {
        return;
      }
    }
    setSelectedTab(tab);
  };

  function renderAccountModal() {
    return (
      <div className="box-sizing-default modal-background">
        <div className="box-sizing-default modals" id="account-modal">
          <div className="box-sizing-default modal-sidebar">
            <ul className="box-sizing-default modal-sidebar-tabs">
              <li
                className={`box-sizing-default modal-sidebar-tab ${
                  selectedAccountModalTab === "Account" ? "active" : ""
                }`}
                onClick={() => setSelectedAccountModalTab("Account")}
              >
                Account
              </li>
              <li
                className={`box-sizing-default modal-sidebar-tab ${
                  selectedAccountModalTab === "Settings" ? "active" : ""
                }`}
                onClick={() => setSelectedAccountModalTab("Settings")}
              >
                Settings
              </li>
              <li
                className={`box-sizing-default modal-sidebar-tab ${
                  selectedAccountModalTab === "Integrations" ? "active" : ""
                }`}
                onClick={() => setSelectedAccountModalTab("Integrations")}
              >
                Integrations
              </li>
            </ul>
          </div>
          <div className="box-sizing-default account-modal-content-container">
            <button
              className="small-icon-button"
              onClick={handleAccountModalClose}
            >
              <img src={xMarkIcon} className="small-icon-button" alt="close" />
            </button>
            {selectedAccountModalTab === "Account" &&
              renderAccountModalAccountTab()}
            {selectedAccountModalTab === "Settings" &&
              renderAccountModalSettingsTab()}
            {selectedAccountModalTab === "Integrations" &&
              renderAccountModalIntegrationsTab()}
          </div>
        </div>
      </div>
    );
  }

  function renderAccountModalAccountTab() {
    const renderTabContent = () => {
      switch (activeAccountTab) {
        case AccountAccountTabs.PROFILE:
          return <>{renderProfileAccount()}</>;
        case AccountAccountTabs.CLINIC:
          return <>{renderClinicAccount()}</>;
        case AccountAccountTabs.SUBSCRIPTION:
          return <>{renderSubscriptionAccount()}</>;
        default:
          return null;
      }
    };

    return (
      <div className="box-sizing-default account-modal-content">
        <div
          style={{
            color: "black",
            fontSize: "2rem",
            fontWeight: "500",
            textAlign: "left",
            fontFamily: "var(--font-family)",
          }}
        >
          Account
        </div>
        <ul className="settings-tab-list">
          {Object.values(AccountAccountTabs).map((tab) => (
            <li
              key={tab}
              className={`settings-tab-list-item ${
                activeAccountTab === tab ? "active" : ""
              }`}
              onClick={() => setActiveAccountTab(tab)}
            >
              {tab}
            </li>
          ))}
        </ul>
        <div>
          {/* <h2>Settings</h2> */}
          {renderTabContent()}
        </div>
      </div>
    );
  }

  const renderProfileAccount = () => {
    return (
      <div>
        <AccountProfileModal
          auth={auth}
          db={db}
          currentUser={currentUser as User}
          updateCurrentUser={updateCurrentUser}
        />
      </div>
    );
  };

  const renderClinicAccount = () => {
    return (
      <div>
        <AccountClinicModal db={db} currentUser={currentUser as User} />
      </div>
    );
  };
  const renderSubscriptionAccount = () => {
    return (
      <div>
        <AccountSubscriptionModal
          auth={auth}
          db={db}
          currentUser={currentUser as User}
        />
      </div>
    );
  };

  function renderAccountModalSettingsTab() {
    const renderTabContent = () => {
      switch (activeSettingsTab) {
        case AccountSettingsTabs.GENERAL:
          return (
            <>
              {
                <SettingsGeneralModal
                  auth={auth}
                  db={db}
                  currentUser={currentUser as User}
                />
              }
            </>
          );
        // case AccountSettingsTabs.NOTE:
        //   return (
        //     <>
        //       {
        //         <SettingsNoteModal
        //           auth={auth}
        //           db={db}
        //           currentUser={currentUser as User}
        //         />
        //       }
        //     </>
        //   );
        case AccountSettingsTabs.LETTER:
          return <LetterHeadUpload userID={uid} />;
        case AccountSettingsTabs.SIGNATURE:
          return <SignatureUpload userID={uid} />;
        // case AccountSettingsTabs.TEMPLATE:
        //   return <TemplateSettings userID={uid} />;)
        default:
          return null;
      }
    };

    return (
      <div className="box-sizing-default account-modal-content">
        <div
          style={{
            color: "black",
            fontSize: "2rem",
            fontWeight: "500",
            textAlign: "left",
            fontFamily: "var(--font-family)",
          }}
        >
          Settings
        </div>
        {/* <p>Coming soon...</p> */}
        <ul className="settings-tab-list">
          {Object.values(AccountSettingsTabs).map((tab) => (
            <li
              key={tab}
              className={`settings-tab-list-item ${
                activeSettingsTab === tab ? "active" : ""
              }`}
              onClick={() => setActiveSettingsTab(tab)}
            >
              {tab}
            </li>
          ))}
        </ul>
        <div>
          {/* <h2>Settings</h2> */}
          {renderTabContent()}
        </div>
      </div>
    );
  }

  function renderAccountModalIntegrationsTab() {
    const handleApiKeyChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      setClinikoApiKey(event.target.value);
    };

    const handleSaveClinikoApiKey = async () => {
      if (clinikoApiKey.length >= 8) {
        try {
          const userDocRef = doc(db, "users", uid);
          await updateDoc(userDocRef, {
            clinikoApiKey: clinikoApiKey,
          });
          // alert("API key saved successfully.");
        } catch (error) {
          console.error("Error saving API key:", error);
          // alert("Failed to save API key.");
        } finally {
          setIsClinikoApiKeyStored(true);
          setIsClinikoApiKeyValid(true);
          setCurrentUser((prevUser) => {
            if (prevUser) {
              return {
                ...prevUser,
                clinikoApiKey: clinikoApiKey,
              };
            }
            return null;
          });
        }
      } else {
        // Handle case where API key is less than 8 characters
        console.error("API key must have a minimum of 8 characters.");
        setIsClinikoApiKeyValid(false);
        // alert("API key must have a minimum of 8 characters.");
      }
    };

    const handleClearClinikoApiKey = async () => {
      try {
        const userDocRef = doc(db, "users", uid);
        await updateDoc(userDocRef, {
          clinikoApiKey: deleteField(),
        });
      } catch (error) {
        console.error("Error clearing Cliniko API key:", error);
      } finally {
        setClinikoApiKey("");
        setIsClinikoApiKeyStored(false);
        setCurrentUser((prevUser) => {
          if (prevUser) {
            return {
              ...prevUser,
              clinikoApiKey: "",
            };
          }
          return null;
        });
      }
    };

    const handleEditClinikoApiKey = () => {
      // Enable the text box for editing the API key
      setIsClinikoApiKeyStored(false);
    };

    const renderClinikoApiKeyInput = () => {
      if (isClinikoApiKeyStored) {
        return (
          <div>
            <input
              type="text"
              value={
                clinikoApiKey?.length > 0
                  ? clinikoApiKey.substring(0, 5) +
                    "*".repeat(clinikoApiKey.length - 5)
                  : ""
              }
              disabled
            />
            <button onClick={handleEditClinikoApiKey}>Edit</button>
            <button
              className="button-small-brand-red"
              style={{ marginLeft: "1rem" }}
              onClick={() => {
                if (
                  window.confirm(
                    "Are you sure you would like to clear your Cliniko API key? This will disable the Cliniko integration."
                  )
                ) {
                  handleClearClinikoApiKey();
                }
              }}
            >
              Clear
            </button>
          </div>
        );
      } else {
        return (
          <div>
            <input
              type="text"
              value={clinikoApiKey}
              onChange={handleApiKeyChange}
            />
            <button
              className="button-small-brand-green"
              onClick={handleSaveClinikoApiKey}
            >
              Save
            </button>
            {!isClinikoApiKeyValid && (
              <div className="box-sizing-default alert-text">
                API key should be at least 8 characters long.
              </div>
            )}
          </div>
        );
      }
    };

    const handleMeddbaseUserIdChange = (
      event: React.ChangeEvent<HTMLInputElement>
    ) => {
      setMeddbaseUserId(event.target.value);
    };

    const handleSaveMeddbaseUserId = async () => {
      if (meddbaseUserId.length >= 6) {
        try {
          const userDocRef = doc(db, "users", uid);
          await updateDoc(userDocRef, {
            meddbaseUserId: meddbaseUserId,
            isMeddbaseUserIdStored: true,
          });
          setMeddbaseUserId(meddbaseUserId);
          setIsMeddbaseUserIdStored(true);
          setIsMeddbaseUserIdValid(true);
          // alert("API key saved successfully.");
        } catch (error) {
          console.error("Error saving API key:", error);
          // alert("Failed to save API key.");
        }
      } else {
        // Handle case where API key is less than 8 characters
        console.error("Meddbase User ID must have a minimum of 6 characters.");
        setIsMeddbaseUserIdValid(false);
        // alert("API key must have a minimum of 8 characters.");
      }
    };

    const handleClearMeddbaseUserId = async () => {
      try {
        const userDocRef = doc(db, "users", uid);
        await updateDoc(userDocRef, {
          meddbaseUserId: deleteField(),
          isMeddbaseUserIdStored: false,
        });
      } catch (error) {
        console.error("Error clearing Meddbase User ID:", error);
      } finally {
        setMeddbaseUserId("");
        setIsMeddbaseUserIdStored(false);
        setMeddbasePID(null);
        setUnactionedHl7Messages([]);
        setCurrentUser((prevUser) => {
          if (prevUser) {
            return {
              ...prevUser,
              meddbaseUserId: "",
              isMeddbaseUserIdStored: false,
            };
          }
          return null;
        });
      }
    };

    const handleEditMeddbaseApiKey = () => {
      // Enable the text box for editing the API key
      setIsMeddbaseUserIdStored(false);
    };

    const renderMeddbaseUserIdInput = () => {
      if (isMeddbaseUserIdStored) {
        return (
          <div>
            <input type="text" value={meddbaseUserId} disabled />
            <button onClick={handleEditMeddbaseApiKey}>Edit</button>
            <button
              className="button-small-brand-red"
              style={{ marginLeft: "1rem" }}
              onClick={() => {
                if (
                  window.confirm(
                    "Are you sure you would like to clear your Meddbase user ID? This will disable the Meddbase integration."
                  )
                ) {
                  handleClearMeddbaseUserId();
                }
              }}
            >
              Clear
            </button>
          </div>
        );
      } else {
        return (
          <div>
            <input
              type="text"
              value={meddbaseUserId}
              onChange={handleMeddbaseUserIdChange}
            />
            <button
              className="button-small-brand-green"
              onClick={handleSaveMeddbaseUserId}
            >
              Save
            </button>
            {!isMeddbaseUserIdValid && (
              <div className="box-sizing-default alert-text">
                Meddbase User ID should be at least 6 characters long.
                {/* TODO: Check how long the User ID is */}
              </div>
            )}
          </div>
        );
      }
    };

    return (
      <div className="box-sizing-default account-modal-content">
        <h2>Integrations</h2>
        {/* <p>Coming soon...</p> */}
        {/* <div
          className="alert-text"
          style={{ fontSize: "1rem", marginBottom: "1rem" }}
        >
          Please note that integrations are in BETA.
        </div> */}
        {/* Cliniko Integration */}
        <div style={{ fontSize: "1.5rem", fontWeight: "500" }}>Cliniko</div>
        <hr
          style={{
            margin: "0 0 1rem 0",
            border: "none",
            borderTop: "1px solid #e0e0e0",
          }}
        />
        <p>Instructions to integrate with Cliniko:</p>
        <ol style={{ lineHeight: "2" }}>
          <li>Sign in to your Cliniko account.</li>
          <li>
            Go to My Info on Cliniko by clicking on your name on the left
            navigation bar.
          </li>
          <li>Enable "allow yourself to create and use API keys".</li>
          <li>Go to Manage API keys.</li>
          <li>
            Add an API key and give this API key a name (e.g., Motics Copilot).
          </li>
          <li>Create API key and copy the new API key.</li>
          <li>
            Paste the API key below.
            {renderClinikoApiKeyInput()}
          </li>
        </ol>
        {/* Meddbase Integration */}
        <div
          style={{ fontSize: "1.5rem", fontWeight: "500", marginTop: "2rem" }}
        >
          Meddbase
        </div>
        <hr
          style={{
            margin: "0 0 1rem 0",
            border: "none",
            borderTop: "1px solid #e0e0e0",
          }}
        />
        <p>
          Meddbase integration is available only to users who have specifically
          requested this bespoke service.
          {/* TODO: Check with Adam Goode if this is the way to view their user ID */}
        </p>
        {currentUser?.meddbaseEnabled && (
          <>
            <p>Instructions to activate integration with Meddbase:</p>
            <ol style={{ lineHeight: "2" }}>
              <li>Sign in to your Meddbase account.</li>
              <li>
                Click on "Profile" at the bottom of the left navigation bar.
              </li>
              <li>Locate your Meddbase User ID and copy it.</li>
              <li>
                Paste your Meddbase User ID below.
                {renderMeddbaseUserIdInput()}
              </li>
            </ol>
          </>
        )}
        <div
          style={{
            margin: "3rem 0",
            textAlign: "center",
            fontWeight: "bold",
          }}
        >
          Don't see the integration you need? Discuss your needs with us via
          chat or email at{" "}
          <a href="mailto:hello@motics.co.uk">hello@motics.co.uk</a>.
        </div>
      </div>
    );
  }

  const handleCloseMigrationBanner = () => {
    setShowMigrationBanner(false);
    localStorage.setItem("migrationBannerVisible", "false");
  };

  // Reset banner visibility when user logs in
  useEffect(() => {
    if (uid) {
      localStorage.setItem("migrationBannerVisible", "true");
      setShowMigrationBanner(true);
    }
  }, [uid]);

  return (
    <>
      {showMigrationBanner && (
        <MigrationBanner onClose={handleCloseMigrationBanner} />
      )}
      {showBannerNotification && meddbasePID && (
        <BannerNotification
          message={bannerNotificationMessage}
          onClose={handleCloseNotification}
          onStartSession={handleStartSession}
        />
      )}
      <TopBar
        onAvatarClick={handleAvatarClick}
        userInitials={userInitials}
        userEmail={userEmail}
        isLeftTabCollapsed={isLeftTabCollapsed}
        toggleLeftTab={toggleLeftTab}
      />
      <div className="box-sizing-default after-top-bar-container">
        <LeftTab
          handleTabSelect={handleTabSelect}
          selectedTab={selectedTab}
          isLeftTabCollapsed={isLeftTabCollapsed}
        />
        <div className="box-sizing-default tab-content-container">
          {isAvatarDropdownOpen && (
            <div ref={dropdownMenuRef}>
              <DropdownMenu
                onAccountClick={handleAccountClick}
                onSignOut={handleSignOut} // This is the sign out function you already have in the Home component
              />
            </div>
          )}
          {selectedTab === "patients" && <PatientsTabContent uid={uid} />}
          {selectedTab === "transcribe" && (
            <TranscribeTabContent
              uid={uid}
              user={user}
              clinikoApiKey={
                currentUser !== null ? currentUser.clinikoApiKey || "" : ""
              }
              recording={recording}
              currentUser={currentUser as User}
              setRecording={setRecording}
              unactionedHl7Messages={unactionedHl7Messages}
              setUnactionedHl7Messages={setUnactionedHl7Messages}
              meddbasePID={meddbasePID}
              setMeddbasePID={setMeddbasePID}
              meddbasePV1={meddbasePV1}
            />
          )}
          {selectedTab === "search" && <SearchTabContent />}
          {selectedTab === "history" && (
            <HistoryTabContent
              uid={uid}
              user={user}
              currentUser={currentUser as User}
              clinikoApiKey={
                currentUser !== null ? currentUser.clinikoApiKey || "" : ""
              }
              unactionedHl7Messages={unactionedHl7Messages}
              setUnactionedHl7Messages={setUnactionedHl7Messages}
              meddbasePID={meddbasePID}
              setMeddbasePID={setMeddbasePID}
              meddbasePV1={meddbasePV1}
            />
          )}
          {selectedTab === "templates" && <TemplatesTabContent userID={uid} />}
          {selectedTab === "help" && <HelpTabContent />}
        </div>
      </div>
      {isAccountModalOpen && renderAccountModal()}
    </>
  );
}

export default Home;
